import type { FC } from "react";
import { useRef } from "react";

import classNames from "classnames";

import { PackageIcon } from "@jti/brand-icons/package";
import { Icon } from "@jti/core-components";
import { formatPrice } from "@jti/format-price";
import { I18n } from "@jti/ui";

import styles from "./FreeShippingProgressBar.module.scss";

export type FreeShippingProgressBarProps = {
  current?: number;
  threshold?: number | undefined;
  currency?: string;
  className?: string;
  dataTestId?: string;
  keepVisibleWhenInitiallyReached?: boolean;
};

export const FreeShippingProgressBar: FC<FreeShippingProgressBarProps> = ({
  current,
  threshold,
  currency = "",
  className,
  dataTestId = "freeShippingProgressFill",
  keepVisibleWhenInitiallyReached = false,
}: FreeShippingProgressBarProps) => {
  const currentValue = current;
  const wasInitiallyAboveThreshold = useRef<boolean | null>(null);

  if (wasInitiallyAboveThreshold.current === null && currentValue != null && threshold != null) {
    wasInitiallyAboveThreshold.current = currentValue >= threshold;
  }

  if (!threshold || currentValue == null) {
    return null;
  }

  if (!keepVisibleWhenInitiallyReached && wasInitiallyAboveThreshold.current && currentValue >= threshold) {
    return null;
  }

  const remainingAmount = threshold - currentValue;
  const isThresholdReached = remainingAmount <= 0;

  const remainingAmountLabel = formatPrice({
    currency,
    value: Math.max(0, remainingAmount),
  });

  const thresholdLabel = formatPrice({
    currency,
    value: threshold,
    decimalDigitsOverride: 0,
  });

  const progress = Math.min(100, Math.max(0, (currentValue / threshold) * 100));

  const label = isThresholdReached ? (
    <I18n t="cart.free_shipping.progress.reached">
      <strong>
        <I18n t="cart.free_shipping.progress.reached.free" />
      </strong>
    </I18n>
  ) : (
    <I18n t="cart.free_shipping.progress.remaining">
      <strong>{remainingAmountLabel}</strong>
      <strong>
        <I18n t="cart.free_shipping.progress.remaining.free" />
      </strong>
    </I18n>
  );

  return (
    <div className={classNames(styles.container, className)} data-testid="freeShippingBar">
      <div className={styles.label}>
        <Icon icon={PackageIcon} size="md" aria-hidden="true" />
        <span role="status">{label}</span>
      </div>
      <div className={styles.progressRow}>
        <div
          className={styles.track}
          aria-label="Free shipping progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={threshold}
          aria-valuenow={Math.round(currentValue)}
          data-testid={dataTestId}
        >
          <div className={styles.fill} style={{ width: `${progress}%` }} />
        </div>
        <div className={styles.threshold} data-testid="freeShippingThreshold">
          {thresholdLabel}
        </div>
      </div>
    </div>
  );
};
